/// <amd-module name="Test/AttachmentsView/Components/AttachmentsView" />
import * as React from "react";
import { AttachmentsViewActionsCreator } from "Test/AttachmentsView/Actions/AttachmentsViewActionsCreator";
import { AttachmentsViewActionsHub } from "Test/AttachmentsView/Actions/AttachmentsViewActionsHub";
import { AttachmentModelType, AttachmentType } from "Test/AttachmentsView/Attachment";
import * as Resources from "Test/AttachmentsView/Resources";
import { AttachmentsLogStoreViewSource } from "Test/AttachmentsView/Source/AttachmentsLogStoreViewSource";
import { AttachmentsViewSource } from "Test/AttachmentsView/Source/AttachmentsViewSource";
import { AttachmentsViewStore, IAttachmentsViewState } from "Test/AttachmentsView/Store/AttachmentsViewStore";
import * as TelemetryConstants from "Test/AttachmentsView/TelemetryConstants";
import { IViewContextData } from "Test/common-util/Common";
import { TestLogListItem } from "Test/common-util/Contracts/ExtendedTest";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import * as Telemetry from "Test/common-util/Telemetry";
import * as Utils_String from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IVssComponentContext, IVssComponentProperties, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IEditorProps } from "VSS/Features/Monaco/Contracts";
import { Button } from "VSSUI/Button";
import { ISplitterProps, Splitter, SplitterElementPosition } from "VSSUI/Components/Splitter";
import { Icon } from "VSSUI/Icon";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ago } from "VSSUI/Utilities/Date";
import { IMenuItem } from "VSSUI/Menu";
import { ColumnSelect, ColumnMore, ITableColumn, Table, renderLoadingCell, TwoLineTableCell, SimpleTableCell } from "VSSUI/Table";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { ISelectionRange } from "VSSUI/Utilities/Selection";
import { Intersection } from "VSSUI/Intersection";
import { Observer } from "VSSUI/Observer";

export interface IAttachmentsViewProps extends IVssComponentProperties {
    testRunId: number;
    testResultId: number;
    subResultId: number;
    viewContext: IViewContextData;
    isFullScreen: boolean;
}

interface IAttachmentRow extends TestLogListItem {
    isNewRunsRow?: boolean;
    isLoadItem?: boolean;
    renderRow?: (index: number, item: TestLogListItem, details: IAttachmentRow) => JSX.Element;
}

export class AttachmentsView extends VssComponent<IAttachmentsViewProps, IAttachmentsViewState, IVssComponentContext> {
    private _pageContext: IVssComponentContext;
    private _store: AttachmentsViewStore;
    private _columnSelect: ColumnSelect;
    private _actionCreator: AttachmentsViewActionsCreator;
    private _lastActiveItem: TCMContracts.TestAttachment | TestLogListItem | null;
    private _attachmentTypeExtension = "";
    private _editor?: any;
    private _listItems: ObservableArray<TCMContracts.TestAttachment> | ObservableArray<IAttachmentRow | ObservableValue<IAttachmentRow | undefined>>;
    private _refAttachmentTab: any;
    private _refAttachmentTable = React.createRef<Table<TCMContracts.TestAttachment | IAttachmentRow | IAttachmentRow | undefined>>();
    private _shouldShowBothPanel = new ObservableValue<boolean>(false);
    private _defaultAttachmentPaneWidth: number = 644;
    private _attachmentListDefaultWidth: number = 320;
    private _refAttachmentContainer: any;

    constructor(props: any, pageContext: IVssComponentContext) {
        super(props);

        const attachmentsViewActionsHub = new AttachmentsViewActionsHub();
        const attachmentsViewSource = new AttachmentsViewSource();
        const attachmentsLogStoreViewSource = new AttachmentsLogStoreViewSource();
        this._store = new AttachmentsViewStore(attachmentsViewActionsHub);
        this._columnSelect = new ColumnSelect();
        this._pageContext = pageContext;
        this._actionCreator = new AttachmentsViewActionsCreator(this._pageContext.pageContext, attachmentsViewActionsHub, attachmentsViewSource, attachmentsLogStoreViewSource);
        this._lastActiveItem = null;
        this._refAttachmentContainer = React.createRef();
    }

    public componentWillMount(): void {
        this._handleStoreChange();
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._handleStoreChange);
        this._store.dispose();
    }

    public componentDidMount(): void {
        this._store.addChangedListener(this._handleStoreChange);
        this._actionCreator.getAttachments(this.props.testRunId, this.props.testResultId, this.props.subResultId);
        this._updateStatusToShowBothPane();
    }

    public componentDidUpdate(): void {
        this._onEditorSizeChanged();
        this._updateStatusToShowBothPane();
        if (this._shouldShowBothPanel.value && this.state.isOnlyAttachmentPreviewMode) {
            this._actionCreator.updateOnlyPreviewModeSelection(false);
        }
        if (!this.state.isOnlyAttachmentPreviewMode && !!this._refAttachmentTable.current
            && this.state.selection && this.state.selection.value[0]){
            this._refAttachmentTable.current.scrollIntoView(this.state.selection.value[0].beginIndex);
        }

        if(!!this._refAttachmentContainer.current && this.state.isOnlyAttachmentPreviewMode){
            this._refAttachmentContainer.current.focus();
        }
    }

    public render(): JSX.Element {
        const isAttachmentsLoading: boolean = this.state.isAttachmentsListLoading || this.state.isLogStoreAttachmentsListLoading;
        const noAttachments: boolean = !this.state.attachments || (this.state.attachments &&
            !((this.state.attachments.getLegacyAttachments() && this.state.attachments.getLegacyAttachments().length > 0) ||
                (this.state.attachments.getLogStoreAttachments() && this.state.attachments.getLogStoreAttachments().length > 0)));
        const hideSplitter: boolean = !!this.state.errorMessage || isAttachmentsLoading || noAttachments;
        let splitterProps: ISplitterProps;

        return (
            <div className="attachment-view-container">
                {
                    isAttachmentsLoading && this._getLoadingAttachmentsView()
                }
                {
                    this.state.errorMessage && this._getErrorMessageElement(this.state.errorMessage)
                }
                {
                    !this.state.errorMessage && !isAttachmentsLoading && noAttachments && this._getNoAttachmentsView()
                }
                <div className="attachment-view-attachments-area" hidden={hideSplitter}
                    ref={(refAttachmentTab: HTMLDivElement) => (this._refAttachmentTab = refAttachmentTab)}>
                    <Observer shouldShowPreview={this._shouldShowBothPanel}>
                        {(val: { shouldShowPreview: boolean }) => {
                            splitterProps = this._getSplitterProps(val.shouldShowPreview);
                            return (
                                <div className="attachment-view-body flex">
                                    <Splitter {...splitterProps} />
                                </div>);
                        }
                        }
                    </Observer>
                </div>
            </div >);
    }

    private _getSplitterProps(shouldShowBothPane: boolean): ISplitterProps {
        if (shouldShowBothPane) {
            return {
                fixedElement: SplitterElementPosition.Near,
                onRenderNearElement: this._renderLeftPanel,
                onRenderFarElement: this._renderRightPanel,
                farElementClassName: "attachments-preview",
                initialFixedSize: this._attachmentListDefaultWidth,
                minFixedSize: this._attachmentListDefaultWidth,
                maxFixedSize: Math.max(this._attachmentListDefaultWidth, !!this._refAttachmentTab ? this._refAttachmentTab.clientWidth - this._attachmentListDefaultWidth : this._attachmentListDefaultWidth),
                onFixedSizeChanged: this._onEditorSizeChanged,
                className: "horizontal test-Attachment-overlay-panel-component"
            } as ISplitterProps;
        }
        else if (this.state.isOnlyAttachmentPreviewMode) {        
            return {
                fixedElement: SplitterElementPosition.Near,
                onRenderNearElement: this._renderLeftPanel,
                onRenderFarElement: this._previewAttachmentOnFullPane,
                farElementClassName: "attachments-preview",
                // This is the case when we are previewing only.
                // To keep the last selected element we always render attachment list as a result we are keeping splitter there.
                // Since we only want to show preview so hiding attachment list so that tab doesnt go there when using keyborad.
                nearElementClassName: "hide-left-container",
                maxFixedSize: 0,
                onFixedSizeChanged: this._onEditorSizeChanged,
                disabled: true,
                className: "horizontal test-Attachment-overlay-panel-component"
            } as ISplitterProps;
        }
        else {
            return {
                fixedElement: SplitterElementPosition.Far,
                onRenderNearElement: this._renderLeftPanel,
                className: "horizontal test-Attachment-overlay-panel-component"
            } as ISplitterProps;
        }
    }

    private _updateStatusToShowBothPane() {
        //left pane 320px +  divider 4px + right pane 320px --> 320 px is enough space to see attachment 
        let hasEnoughSpaceForBothPane = !!this._refAttachmentTab && this._refAttachmentTab.clientWidth >= this._defaultAttachmentPaneWidth;
        if (hasEnoughSpaceForBothPane) {
            this._shouldShowBothPanel.value = true;
        } else {
            this._shouldShowBothPanel.value = false;
        }
    }

    private _renderLeftPanel = (): JSX.Element => {
        if (!!this.state && !!this.state.attachments && !this._lastActiveItem) {
            const legacyAttachments = this.state.attachments.getLegacyAttachments();
            this._listItems = new ObservableArray<TCMContracts.TestAttachment>(legacyAttachments);
        }
        // Use containerClassName field in Table element if scrollbar styling, etc. needs to be changed in the future
        return (
            <div className="attachment-view-left-pane">
                <Intersection>
                    <div className="attachment-view-table-div">
                        {
                            this.state.attachments &&
                            <Table
                                ref={this._refAttachmentTable}
                                itemProvider={this._listItems}
                                columns={this._getColumnsForDetailsList()}
                                showLines={false}
                                singleClickActivation={true}
                                pageSize={15}
                                onActivate={this._onActiveItemChangedCaller}
                                selection={this.state.selection}
                            />
                        }
                    </div>
                </Intersection>
            </div>
        );
    }

    private _renderRightPanel = (): JSX.Element => {
        let attachmentType = AttachmentType.notPreviewable;
        if (!!this.state && !!this.state.lastSelectedAttachment) {
            if (this.state.lastSelectedAttachment.attachmentType === AttachmentModelType.Legacy || (this.state.lastSelectedAttachment.attachmentType === AttachmentModelType.Logstore &&
                isFeatureFlagEnabled(this.context.pageContext, this._previewLogStoreAttachmentRunFF, false))) {
                attachmentType = this._getAttachmentType(this.state.lastSelectedAttachment.filePath);
            }
        }

        if (this.state.errorMessageInPreview) {
            return (
                <div className="attachment-view-right-pane flex-grow">
                    {
                        this._getErrorMessageElement(this.state.errorMessageInPreview)
                    }
                </div>);
        }

        // This code handles rendering of the preview when the page is first loaded
        if (!!this.state && !!this.state.lastSelectedAttachment && !this.state.isAttachmentPreviewLoading) {
            if (this.state.content == null && attachmentType == AttachmentType.previewableText) {
                if (this._isItemInLogstore(this.state.lastSelectedAttachment.attachment)) {
                    this._actionCreator.showLogstoreAttachmentPreview(
                        this.props.testRunId,
                        this.props.testResultId,
                        this.props.subResultId,
                        (this.state.lastSelectedAttachment.attachment as TCMContracts.TestAttachment).fileName,
                        (this.state.lastSelectedAttachment.attachment as TCMContracts.TestAttachment).size,
                        (this.state.lastSelectedAttachment.attachment as TCMContracts.TestAttachment).id
                    );
                }
                this._actionCreator.showAttachmentPreview(
                    this.state.lastSelectedAttachment.attachment.id,
                    this.props.testRunId,
                    this.props.testResultId,
                    this.props.subResultId
                );
            }
        }

        let legacyAttachments = null;
        let logStoreAttachments = null;

        if (this.state.attachments) {
            legacyAttachments = this.state.attachments.getLegacyAttachments();
            logStoreAttachments = this.state.attachments.getLogStoreAttachments();
        }

        return (
            <div className="attachment-view-right-pane flex-grow"
                tabIndex={0}                
                ref={this._refAttachmentContainer}
                aria-label={(this.state.isAttachmentPreviewLoading === false && attachmentType === AttachmentType.previewableImage) && this.state.lastSelectedAttachment
                            ? "Attachment preview is Image" : ""}
                hidden={this.state.attachments === null ||
                    ((this.state.isAttachmentsListLoading || !legacyAttachments || legacyAttachments.length <= 0)
                        && (this.state.isLogStoreAttachmentsListLoading || !logStoreAttachments || logStoreAttachments.length <= 0))
                }>
                {
                    (!this.state.isAttachmentPreviewable || attachmentType === AttachmentType.notPreviewable) &&
                    this._getAttachmentUnpreviewableView()
                }
                {
                    this.state.isAttachmentPreviewLoading &&
                    this._getAttachmentPreviewLoadingView()
                }
                {
                    this.state.content != null && !this.state.isAttachmentPreviewLoading && attachmentType === AttachmentType.previewableText &&
                    this._getDefaultEditor()
                }
                {
                    (this.state.isAttachmentPreviewLoading === false && attachmentType === AttachmentType.previewableImage) && this.state.lastSelectedAttachment &&
                    this._previewAttachmentOfTypeImage(this.state.lastSelectedAttachment.url, this.state.lastSelectedAttachment.filePath)
                }
            </div>);
    }

    private _getDefaultEditor(): JSX.Element {
        const legacyComponentLoading: () => JSX.Element = () => <Spinner size={SpinnerSize.large} label={Resources.LoadingAttachmentMessage} />;
        return (
            <WrappedComponent<IEditorProps>
                dependencies={["ms.vss-features.editor"]}
                wrappedType="code-editor"
                wrappedProps={{
                    className: "attachments-monaco-editor",
                    editorOptions: {
                        readOnly: true,
                        minimap: {
                            enabled: false
                        }
                    },
                    monacoTheme: "vs",
                    content: {
                        content: this.state.content!
                    },
                    editorRef: this._onEditorCreated
                }}
                loadingComponent={legacyComponentLoading}
            />
        );
    }

    private _getNoAttachmentsView(): JSX.Element {
        return (
            <div className="no-attachments-area flex-column">
                <div className="no-attachments-icon">
                    <Icon
                        iconName="Attach"
                        className="no-attachments-icon"
                    />
                </div>
                <div className="no-attachments-message">
                    {Resources.NoAttachmentsMessage}
                </div>
            </div>
        );
    }

    private _getAttachmentUnpreviewableView(): JSX.Element {
        return (
            <div className="not-previewable-attachment-area flex-column">
                <div className="attachment-unpreviewable-icon">
                    <Icon
                        iconName="Preview"
                    />
                </div>
                <div className="attachment-unpreviewable-message">
                    {Resources.AttachmentUnpreviewableMessage}
                </div>
                <div className="attachment-unpreviewable-suggestion-message">
                    {this.state.lastSelectedAttachment && this._getDownloadButton()}
                </div>
            </div>
        );
    }

    private _getDownloadButton(): JSX.Element {
        let logstoreArray: TestLogListItem[] = [];
        if (!!this.state.attachments) {
            logstoreArray = this.state.attachments.getLogStoreAttachments();
        }
        const legacyValid = this._isEntityInLegacy();
        return (
            <Button
                className={"attachment-download-button"}
                target="_self"
                primary={true}
                rel="nofollow noopener noreferrer"
                onClick={() => {
                    if (!!this.state.lastSelectedAttachment) {
                        const logStoreValid = this._isItemInLogstore(this.state.lastSelectedAttachment.attachment);
                        if (this._isOddEvenAttachmentIdDisableFF()) {
                            this._actionCreator.downloadSelectedLogStoreAttachments(this.props.testRunId,
                                this.props.testResultId,
                                this.props.subResultId,
                                this.state.lastSelectedAttachment.attachment.fileName,
                                this.state.lastSelectedAttachment.attachment.url);
                        } else {
                            if (logStoreValid) {
                                this._actionCreator.downloadSelectedLogStoreAttachments(this.props.testRunId, this.props.testResultId, this.props.subResultId, this.state.lastSelectedAttachment.attachment.fileName, this.state.lastSelectedAttachment.attachment.url);
                            }
                            else if (legacyValid) {
                                const itemToDownload: TCMContracts.TestAttachment[] = [this.state.lastSelectedAttachment.attachment];
                                this._actionCreator.downloadSelectedAttachments(itemToDownload);
                            }
                        }
                    }
                }}
            >
                {Resources.Download}
            </Button>
        );
    }

    private _getLoadingAttachmentsView(): JSX.Element {
        return (
            <div className="attachment-view-pane-loading">
                <Spinner
                    ariaLabel={Resources.LoadingAttachmentsMessage}
                    className="attachments-view-loading-spinner test-result-loading-spinner-separator"
                    size={SpinnerSize.large}
                    label={Resources.LoadingAttachmentsMessage}
                />
            </div>);
    }

    private _getErrorMessageElement(errorMessage: string): JSX.Element {
        return (
            <div className="attachment-preview-error-message">
                <MessageBar
                    severity={MessageBarSeverity.Error}
                    className="attachment-preview-error-message-bar"
                    onDismiss={this._onErrorMessageDismiss}>
                    {errorMessage.toLocaleString()}
                </MessageBar>
            </div>
        );
    }

    private _getAttachmentPreviewLoadingView(): JSX.Element {
        return (
            <div className="attachment-preview-loading">
                <Spinner
                    ariaLabel={Resources.LoadingAttachmentMessage}
                    className="attachment-preview-loading-spinner"
                    size={SpinnerSize.large}
                    label={Resources.LoadingAttachmentMessage}
                />
            </div>
        );
    }

    private _onEditorCreated = (editor: any) => {
        if (editor) {
            this._editor = editor;
        }
    }

    private _onEditorSizeChanged = (): void => {
        if (this._editor) {
            this._editor.layout();
        }
    }

    private _onErrorMessageDismiss = () => {
        this._actionCreator.closeErrorMessage();
    }

    private _onActiveItemChangedCaller = (): void => {
        let item;
        let model;
        let fileName;
        const logStoreValid = this._isEntityInLogstore();
        if (!!this.state && !!this.state.attachments) {
            let itemIndex = this.state.selection.value[0].beginIndex;
            if (logStoreValid) {
                item = this._listItems.value[itemIndex] as TestLogListItem;
                model = AttachmentModelType.Logstore;
                fileName = item.testLog.logReference.filePath;
            }
            else {
                item = this._listItems.value[itemIndex] as TCMContracts.TestAttachment;
                model = AttachmentModelType.Legacy;
                fileName = item.fileName;
            }
            this._onActiveItemChanged(item, fileName, model);
        }
    }

    private _onActiveItemChanged(item: TCMContracts.TestAttachment | TestLogListItem, filePath: string, attachmentModelType: AttachmentModelType): void {
        const isInvokedItemInSelection: boolean = this._isInvokedItemInSelection(item, attachmentModelType);
        // We need this check to handle active item change events on click of context menu. When the context menu is clicked, the active item changed event is again raised
        // which re-renders the component.
        if (this.state.shouldInvokeFirstItem || ((item !== this._lastActiveItem || !this._shouldShowBothPanel.value) && isInvokedItemInSelection)) {
            this._lastActiveItem = item;
            const attachmentType = this._getAttachmentType(filePath);

            // Add telemetry to get attachment type extension and to see if the attachment is previewable or not.
            Telemetry.publishDetailsPaneEvents(
                this.context.pageContext,
                this.props.viewContext,
                this.props.isFullScreen,
                TelemetryConstants.featureTestTab_PreviewTestAttachment, {
                "RunId": this.props.testRunId,
                "ResultId": this.props.testResultId,
                "SubResultId": this.props.subResultId,
                "previewable": (attachmentType !== AttachmentType.notPreviewable),
                "AttachmentTypeExtension": this._attachmentTypeExtension
            });

            this._actionCreator.onActiveItemChanged(item, attachmentModelType, attachmentType, this.props.testRunId, this.props.testResultId, this.props.subResultId);

            if (!this._shouldShowBothPanel.value) {
                this._actionCreator.updateOnlyPreviewModeSelection(true);
            }
            else {
                this._actionCreator.updateOnlyPreviewModeSelection(false);
            }
        }
    }

    private _previewAttachmentOfTypeImage(url: string, fileName: string): JSX.Element {
        return (
            <div className="attachment-image-container">
                <img
                    className="attachment-image"
                    src={url}
                    alt={fileName}>
                </img>
            </div>
        );
    }

    private _previewAttachmentOnFullPane = (): JSX.Element => {
        return (
            <div className="attachment-preview-mode overlay-right-section" >                
                <div className="overlay-panel-buttons-container">
                    {
                        <Button
                            subtle={true}
                            ariaLabel={Resources.CloseAttachment}
                            iconProps={{ iconName: "Cancel" }}
                            onClick={this._closePanel}
                            tooltipProps={{ showOnFocus: true, text: Resources.CloseAttachment }}
                        />}
                </div>
                {this._renderRightPanel()}
            </div>
        );
    }

    private _closePanel = () => {
        this._actionCreator.updateOnlyPreviewModeSelection(false);
    }

    private _getSelectedIndices = (): number[] => {
        let selectedIndices: number[] = [];
        if (!!this.state && !!this.state.attachments) {
            let length: number = this.state.selection.value.length;
            for (let i = 0; i < length; i++) {
                let selectionRange: ISelectionRange = this.state.selection.value[i];
                if (selectionRange) {
                    for (let j = selectionRange.beginIndex; j <= selectionRange.endIndex; j++) {
                        selectedIndices.push(j);
                    }
                }
            }
        }
        return selectedIndices;
    }

    private _isInvokedItemInSelection(item: TCMContracts.TestAttachment | TestLogListItem, attachmentModelType: AttachmentModelType): boolean {
        let selection;
        if (!!this.state && !!this.state.attachments && !!this.state.selection) {
            let selectedIndices: number[] = this._getSelectedIndices();
            if (attachmentModelType == AttachmentModelType.Legacy) {
                selection = selectedIndices.map(i => this._listItems.value[i]) as TCMContracts.TestAttachment[];
            }
            else {
                selection = selectedIndices.map(i => this._listItems.value[i]) as TestLogListItem[];
            }
            for (let i = 0; i < selection.length; i++) {
                if (attachmentModelType == AttachmentModelType.Legacy) {
                    item = item as TCMContracts.TestAttachment;
                    selection = selection as TCMContracts.TestAttachment[];
                    if (item.id === selection[i].id) {
                        return true;
                    }
                }
                else {
                    item = item as TestLogListItem;
                    selection = selection as TestLogListItem[];
                    if (item.testLog === selection[i].testLog) {
                        return true;
                    }
                }
            }
            return false;
        }
        else {
            return false;
        }
    }

    private _getAttachmentType = (fileName: string): AttachmentType => {
        const _allowedPreviewableFileExtensions: string[] = [".txt", ".log"];
        const _allowedPreviewableImageExtensions: string[] = [".jpg", ".jpeg", ".png", ".bmp", ".gif"];

        this._attachmentTypeExtension = "";
        if (fileName.indexOf(".") !== -1) {
            this._attachmentTypeExtension = fileName.substring(fileName.lastIndexOf("."));
        }

        if (_allowedPreviewableFileExtensions.indexOf(this._attachmentTypeExtension.toLowerCase()) > -1) {
            return AttachmentType.previewableText;
        }
        else if (_allowedPreviewableImageExtensions.indexOf(this._attachmentTypeExtension.toLowerCase()) > -1) {
            return AttachmentType.previewableImage;
        }
        else {
            return AttachmentType.notPreviewable;
        }
    }

    private _columnOnRender = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TCMContracts.TestAttachment>, tableItem: TCMContracts.TestAttachment | TestLogListItem): JSX.Element => {
        const logStoreValid = this._isEntityInLogstore();
        const legacyValid = this._isEntityInLegacy();
        let fileName;
        let size;
        let modifiedOn;
        let topLine: JSX.Element;
        let bottomLine: JSX.Element;
        if (logStoreValid) {
            let tempItem = tableItem as TestLogListItem;
            if (!!tempItem.testLog && !!tempItem.continuationToken) {
                return TwoLineTableCell({ columnIndex, line1: renderLoadingCell(tableColumn.columnLayout), line2: renderLoadingCell(tableColumn.columnLayout) });
            }
            fileName = this._getFileName(tempItem.testLog.logReference.filePath);
            size = tempItem.testLog.size;
            modifiedOn = tempItem.testLog.modifiedOn;
        }
        else if (legacyValid) {
            let tempItem = tableItem as TCMContracts.TestAttachment;
            fileName = tempItem.fileName;
            size = tempItem.size;
            modifiedOn = tempItem.createdDate;
        }
        if (legacyValid || logStoreValid) {
            let addedAgoIcon = <Icon iconName="Clock" className="pipelines-initial-icon" />
            topLine = <span className="fontWeightSemiBold text-ellipsis bolt-table-inline-link-left-padding attachment-name" >{fileName}<br /><br /></span>;
            bottomLine = <div className="attachment-info flex">
                <span className="secondary-text flex-row flex-baseline text-ellipsis bolt-table-inline-link-left-padding pipeline-firefox-fix attachment-size">
                    {this._getAttachmentSizeTextInKB(size ? size : 0)}
                </span>
                <span className="secondary-text flex-row flex-baseline text-ellipsis bolt-table-inline-link-right-padding pipeline-firefox-fix attachment-time-added-ago">
                    {addedAgoIcon}{this._getAttachmentTimeAddedAgo(modifiedOn)}
                </span>
            </div>;
            return <TwoLineTableCell className="bolt-table-cell-content-with-inline-link no-v-padding" columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex} line1={topLine} line2={bottomLine} />;
        }
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div />
            </SimpleTableCell>
        );
    }

    private _getColumnsForDetailsList = (): ITableColumn<any>[] => {
        const columns: ITableColumn<any>[] = [
            this._columnSelect,
            {
                id: "attachments",
                width: -100,
                renderCell: this._columnOnRender
            }
        ];
        const menuItemColumn = new ColumnMore(
            () => {
                return {
                    id: "attachment-view-context-menu-column",
                    items: this._createContextMenuItems()
                };
            },
            undefined,
            (rowindex: number, columnIndex: number) => {
                if (!this.state.selection.selected(rowindex)) {
                    this.state.selection.select(rowindex, 1, false);
                }
            }
        );
        columns.push(menuItemColumn);
        return columns;
    }

    private _createContextMenuItems = (): IMenuItem[] => {
        const items: IMenuItem[] = [];
        if (!!this.state && !!this.state.attachments && !!this.state.selection) {
            const selectedIndices: number[] = this._getSelectedIndices();
            // Leaving selected indices array for future proofing incase we want to allow for multiple selection again
            const itemSelected = this._listItems.value[selectedIndices[0]] as TCMContracts.TestAttachment;
            const logStoreValid = this._isItemInLogstore(itemSelected);
            const legacyValid = this._isEntityInLegacy();
            items.push({
                id: "download-attachment",
                onActivate: () => {
                    if (this._isOddEvenAttachmentIdDisableFF()) {
                        this._actionCreator.downloadSelectedLogStoreAttachments(this.props.testRunId,
                            this.props.testResultId,
                            this.props.subResultId,
                            itemSelected.fileName,
                            itemSelected.url);
                    } else {
                        if (logStoreValid) {
                            this._actionCreator.downloadSelectedLogStoreAttachments(this.props.testRunId, this.props.testResultId, this.props.subResultId, itemSelected.fileName, itemSelected.url);
                        }
                        else if (legacyValid) {
                            this._actionCreator.downloadSelectedAttachments(selectedIndices.map(i => this._listItems.value[i]) as TCMContracts.TestAttachment[]);
                        }
                    }
                },
                iconProps: {
                    iconName: "Download"
                },
                text: Resources.Download
            });
        }
        return items;
    }

    private _getAttachmentSizeTextInKB = (attachmentSize: number): string => {
        return Utils_String.format(Resources.AttachmentSizeValueInKB, Math.ceil(attachmentSize / 1024));
    }

    private _getAttachmentTimeAddedAgo = (createdDate: Date): string => {
        return Utils_String.format(Resources.AttachmentAddedAgo, ago(createdDate));
    }

    private _getFileName(path: string): string | null {
        let parts = this._getPathParts(path);
        return parts ? parts[parts.length - 1] : null;
    }

    private _getPathParts(path: string): string[] | null {
        let parts: string[] = [];
        if (path) {
            parts = path.split('/');
        }

        let valid = parts.length > 0 && parts.every(s => !!s);
        if (!valid) {
            return null;
        }

        return parts;
    }

    private _handleStoreChange = (): void => {
        this.setState(this._store.getState());
    }

    // Keeping the logstore flow for future
    private _isEntityInLogstore = (): boolean => {
        return false;
    }

    private _isItemInLogstore = (item: TCMContracts.TestAttachment): boolean => {
        // Odd ids are from LS; Even Ids are from FS
        // Disabling this FF in case of a mitigation will move all downloads to AT via the compat route
        if (!isFeatureFlagEnabled(this.context.pageContext, this._logStoreAttachmentsFeatureFlag, false)) {
            return false;
        }
        else {
            if (item.id % 2 != 0) {
                return true;
            }
            else {
                return false;
            }
        }
    }

    private _isEntityInLegacy = (): boolean => {
        return !!this.state && !!this.state.attachments && !!this.state.attachments.legacyAttachments && this.state.attachments.legacyAttachments.length > 0;
    }

    private _isOddEvenAttachmentIdDisableFF = (): boolean => {
        return isFeatureFlagEnabled(this.context.pageContext, this._oddEvenAttachmentIdDisableFF, false);
    }

    private _logStoreAttachmentsFeatureFlag: string = "WebAccess.TestManagement.LogStore";
    private _previewLogStoreAttachmentRunFF: string = "WebAccess.TestManagement.PreviewLogStoreAttachment";
    private _oddEvenAttachmentIdDisableFF: string = "WebAccess.TestManagement.OddEvenAttachmentIdDisable";
}

Components.add("test-attachmentsview", AttachmentsView);